import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// TypeScript interfaces
export interface PaymentMethod {
  stripePaymentMethodId: string;
  type: 'card' | 'bank_account';
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  funding?: string;
  isDefault: boolean;
  createdAt: string;
}

export interface SubscriptionItem {
  stripePriceId: string;
  stripeProductId: string;
  quantity: number;
  amount: number;
  currency: string;
  description: string;
}

export interface StripeProduct {
  productId: string;
  priceId: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: string;
  intervalCount: number;
  isSoftwareSubscription: boolean;
  metadata: Record<string, string>;
}

export interface Subscription {
  _id: string;
  siteId: {
    _id: string;
    name: string;
    code: string;
    email: string;
  };
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStart?: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
  items: SubscriptionItem[];
  defaultPaymentMethodId?: string;
  paymentMethods: PaymentMethod[];
  billing: {
    name: string;
    email: string;
    phone: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state?: string;
      postal_code: string;
      country: string;
    };
  };
  pricing: {
    basePrice: {
      amount: number;
      currency: string;
    };
    totalAmount: number;
    tax: {
      amount: number;
      rate: number;
    };
  };
  metadata: {
    createdBy?: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    createdAt: string;
    updatedAt: string;
    lastSyncedAt?: string;
    syncStatus: 'synced' | 'pending' | 'error';
  };
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  status?: string;
  isActive?: boolean;
  isPastDue?: boolean;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  trialEnd?: string;
  nextBillingDate?: string;
  pricing?: {
    basePrice: {
      amount: number;
      currency: string;
    };
    totalAmount: number;
    tax: {
      amount: number;
      rate: number;
    };
  };
}

export interface CreateSubscriptionRequest {
  siteId: string;
  paymentMethodId?: string;
  priceIds: string[];
  startTrial?: boolean;
}

export interface AddPaymentMethodRequest {
  paymentMethodId: string;
  isDefault?: boolean;
}

export interface SetDefaultPaymentMethodRequest {
  paymentMethodId: string;
}

export interface SubscriptionsResponse {
  subscriptions: Subscription[];
  pagination: {
    current: number;
    pages: number;
    total: number;
  };
}

// API Functions
export const subscriptionAPI = {
  getProducts: (siteId: string) =>
    api.get<{ products: StripeProduct[], configuredBasePriceId?: string }>('/subscriptions/products', { params: { siteId } }),
  // Get subscription for a site
  getSubscription: (siteId: string) => 
    api.get<Subscription>(`/subscriptions/subscription/${siteId}`),

  // Create new subscription
  createSubscription: (data: CreateSubscriptionRequest) => 
    api.post<Subscription>('/subscriptions/subscription', data),

  // Update subscription
  updateSubscription: (siteId: string, priceIds: string[]) =>
    api.put<Subscription>(`/subscriptions/subscription/${siteId}`, { priceIds }),

  // Cancel subscription
  cancelSubscription: (siteId: string, cancelAtPeriodEnd = true) => 
    api.delete<Subscription>(`/subscriptions/subscription/${siteId}`, { 
      data: { cancelAtPeriodEnd } 
    }),

  // Get subscription status
  getSubscriptionStatus: (siteId: string) => 
    api.get<SubscriptionStatus>(`/subscriptions/subscription/${siteId}/status`),

  // Get payment methods
  getPaymentMethods: (siteId: string) => 
    api.get<{ paymentMethods: PaymentMethod[], defaultPaymentMethodId?: string }>(`/subscriptions/subscription/${siteId}/payment-methods`),

  // Add payment method
  addPaymentMethod: (siteId: string, data: AddPaymentMethodRequest) => 
    api.post<{ message: string, paymentMethod: PaymentMethod }>(`/subscriptions/subscription/${siteId}/payment-methods`, data),

  // Set default payment method
  setDefaultPaymentMethod: (siteId: string, data: SetDefaultPaymentMethodRequest) => 
    api.put<{ message: string }>(`/subscriptions/subscription/${siteId}/payment-methods/default`, data),

  // Remove payment method
  removePaymentMethod: (siteId: string, paymentMethodId: string) => 
    api.delete<{ message: string }>(`/subscriptions/subscription/${siteId}/payment-methods/${paymentMethodId}`),

  // Get all subscriptions (super admin only)
  getAllSubscriptions: (params?: { page?: number, limit?: number, status?: string, search?: string }) => 
    api.get<SubscriptionsResponse>('/subscriptions/subscriptions', { params }),

  // Sync subscription (webhook handler)
  syncSubscription: (subscriptionId: string) => 
    api.post<{ message: string, subscription: Subscription }>(`/subscriptions/subscription/${subscriptionId}/sync`)
};

export default subscriptionAPI;
